import { Component, ElementRef, ErrorHandler, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { disabled, form, maxLength } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { Observable, of, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitoImageHandlerService, TositeJaNaytettavaSivut } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { asetaArvo } from '../../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { TiedostojenLataamisService } from '../../../_jaettu-angular/service/tiedostojen-lataamis.service'
import { KirjattavaKuitti } from '../../../_jaettu-lemonator/model/kirjanpito'
import { LlmTositeLukuSettijasenyys, LlmTositeLukuTestisetti, LlmTositeLukuToimittajaRekisteritieto, TositteenViitetiedot } from '../../../_jaettu-lemonator/model/llm-tosite-luku-testaus'
import { KirjanpitoUriService } from '../../../_jaettu-lemonator/service/kirjanpito-uri.service'
import { LlmTositeLukuMetatiedotService } from '../../../_jaettu-lemonator/service/llm-tosite-luku-metatiedot.service'
import { callLlmTositeLukuTestaus } from './llm-tosite-luku-testaus.client'
import { asetaViitetiedotLomakkeelle, lueMetatiedotLomakkeelta, luoMetatiedotFormGroup, PAIVAMAARA_REGEX } from './metatiedot-form.component'
import { ryhmitteleSettijasenyydetTositteittain } from './settijasenyys'
import { laskeOdotettuToimittajaTeksti, metatiedotToimittajaValinnaksi, TOIMITTAJA_VALINTA_AUTOMAATTINEN } from './toimittaja-valinta'

export type LisattavaTositeKohde =
  { tyyppi: 'lemonaid'; asiakasAvain: string; asiakasId: string; asiakasNimi: string; tositeAvain: string } | { tyyppi: 'ladattu'; tiedosto: File; mediaTyyppi: string }

export interface LisaaTositteetTestisettiinDialogData {
  kohteet: LisattavaTositeKohde[]
}

export interface LisaaTositteetTestisettiinDialogResult {
  lisatty: number
}

type DialogVaihe = 'lataa' | 'setti' | 'tosite' | 'virhe'

@Component({
  templateUrl: './lisaa-tositteet-testisettiin.dialog.html',
  styleUrls: ['./lisaa-tositteet-testisettiin.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LisaaTositteetTestisettiinDialog implements OnInit, OnDestroy {
  @ViewChild('tositeContainer', { static: false }) tositeContainerRef: ElementRef<HTMLDivElement>

  readonly tyhjaKirjausObservable: Observable<null> = of(null)

  readonly tallennetaan = signal(false)
  readonly vaihe = signal<DialogVaihe>('lataa')
  readonly setit = signal<LlmTositeLukuTestisetti[]>([])
  settiValinta = form(signal<string | null>(null), polku => {
    disabled(polku, () => this.tallennetaan() || !!this.uusiNimi)
  })
  uudenSetinNimi = form(signal(''), polku => {
    maxLength(polku, 120)
  })
  readonly settiAvain = signal<string | null>(null)
  readonly settiNimi = signal('')

  readonly indeksi = signal(0)
  readonly lisatty = signal(0)
  readonly ohitettu = signal(0)
  duplikaatteja = 0

  readonly naytettavatTositteetJaSivut = signal<TositeJaNaytettavaSivut[] | null>(null)
  readonly esikatseluUrl = signal<SafeResourceUrl | null>(null)
  readonly esikatseluPdfUrl = signal<string | null>(null)
  readonly esikatseluOnPdf = signal(false)
  readonly viitetiedot = signal<TositteenViitetiedot | null>(null)
  readonly toimittajaRekisteri = signal<LlmTositeLukuToimittajaRekisteritieto[]>([])
  readonly viitetiedotLataa = signal(false)
  /** Avain = tositeAvain. Sisältää vain lemonaid-kohteiden olemassa olevat jäsenyydet. */
  readonly jasenyydet = signal(new Map<string, LlmTositeLukuSettijasenyys[]>())

  metatiedotForm = luoMetatiedotFormGroup()

  readonly odotettuToimittajaTeksti = signal('Ei arvioida (ladattu tiedosto)')

  private _objectUrl: string | null = null
  private _ngUnsubscribe = new Subject<void>()

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LisaaTositteetTestisettiinDialogData,
    private _dialogRef: MatDialogRef<LisaaTositteetTestisettiinDialog, LisaaTositteetTestisettiinDialogResult>,
    private _firebase: FirebaseLemonator,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _kirjanpitoImageHandlerService: KirjanpitoImageHandlerService,
    private _sanitizer: DomSanitizer,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
    private _metatiedotService: LlmTositeLukuMetatiedotService,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
  ) {}

  async ngOnInit() {
    this.metatiedotForm.valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => this._paivitaOdotettuToimittajaTeksti())
    try {
      const [vastaus] = await Promise.all([callLlmTositeLukuTestaus(this._firebase, 'haeSetit', {}), this._lataaJasenyydet()])
      if (vastaus.e) {
        this.vaihe.set('virhe')
        return
      }
      this.setit.set(vastaus.setit)
      asetaArvo(this.settiValinta, this.setit()[0]?.avain ?? null)
      this.vaihe.set('setti')
    } catch (err) {
      this._errorHandler.handleError(err)
      this.vaihe.set('virhe')
    }
  }

  ngOnDestroy() {
    this._vapautaEsikatselu()
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  get uusiNimi(): string {
    return this.uudenSetinNimi().value().trim()
  }

  get nykyinenKohde(): LisattavaTositeKohde | null {
    return this.data.kohteet[this.indeksi()] ?? null
  }

  /** Nykyisen kohteen jäsenyys valitussa setissä, jos tosite on siellä jo. */
  get nykyinenJoValitussaSetissa(): boolean {
    return !!this._nykyisenJasenyydet().find(j => j.settiAvain === this.settiAvain())
  }

  /** Nykyisen kohteen jäsenyys jossakin muussa setissä; sen metatiedot esitäytetään lomakkeelle. */
  get nykyisenMuuJasenyys(): LlmTositeLukuSettijasenyys | null {
    return this._nykyisenJasenyydet().find(j => j.settiAvain !== this.settiAvain()) ?? null
  }

  valituistaJoSetissa(settiAvain: string): number {
    let lkm = 0
    for (const kohde of this.data.kohteet) {
      if (
        kohde.tyyppi === 'lemonaid' &&
        this.jasenyydet()
          .get(kohde.tositeAvain)
          ?.some(j => j.settiAvain === settiAvain)
      ) {
        lkm++
      }
    }
    return lkm
  }

  voiValitaSetin(): boolean {
    return this.vaihe() === 'setti' && (!!this.uusiNimi || !!this.settiValinta().value())
  }

  async valitseSetti() {
    if (!this.voiValitaSetin()) {
      return
    }
    this.tallennetaan.set(true)
    try {
      let settiAvain = this.settiValinta().value()
      let settiNimi = this.setit().find(s => s.avain === settiAvain)?.nimi ?? ''
      if (this.uusiNimi) {
        const luotu = await callLlmTositeLukuTestaus(this._firebase, 'luoSetti', { nimi: this.uusiNimi })
        if (luotu.e === 'nimi-varattu') {
          this._snackbar.open('Samanniminen setti on jo olemassa', 'OK', { duration: 5000 })
          return
        }
        if (luotu.e || !luotu.avain) {
          this._snackbar.open('Uuden setin luonti epäonnistui', 'OK', { duration: 5000 })
          return
        }
        settiAvain = luotu.avain
        settiNimi = this.uusiNimi
      }
      if (!settiAvain) {
        return
      }
      this.settiAvain.set(settiAvain)
      this.settiNimi.set(settiNimi)
      this.vaihe.set('tosite')
      await this._aloitaKohde(0)
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Setin valinta epäonnistui', 'OK', { duration: 5000 })
    } finally {
      this.tallennetaan.set(false)
    }
  }

  kopioi(kentta: 'toimittaja' | 'businessId' | 'paivamaara' | 'summa') {
    if (!this.viitetiedot()) {
      return
    }
    asetaViitetiedotLomakkeelle(this.metatiedotForm, kentta, this.viitetiedot())
  }

  async tallennaJaSeuraava() {
    const kohde = this.nykyinenKohde
    if (!kohde || !this.settiAvain() || this.tallennetaan() || this.nykyinenJoValitussaSetissa) {
      return
    }
    const metatiedot = lueMetatiedotLomakkeelta(this.metatiedotForm)
    if (metatiedot.paivamaara && !PAIVAMAARA_REGEX.test(metatiedot.paivamaara)) {
      this._snackbar.open('Päivämäärän muoto on YYYY-MM-DD', 'OK', { duration: 5000 })
      return
    }
    this.tallennetaan.set(true)
    try {
      const vastaus =
        kohde.tyyppi === 'lemonaid'
          ? await callLlmTositeLukuTestaus(this._firebase, 'lisaaTestitapaus', {
              settiAvain: this.settiAvain(),
              metatiedot,
              tosite: { asiakasAvain: kohde.asiakasAvain, tositeAvain: kohde.tositeAvain },
            })
          : await callLlmTositeLukuTestaus(this._firebase, 'lisaaTestitapaus', {
              settiAvain: this.settiAvain(),
              metatiedot,
              tiedosto: { nimi: kohde.tiedosto.name, mediaTyyppi: kohde.mediaTyyppi, base64Sisalto: await this._tiedostojenLataamisService.getAsDataUrl(kohde.tiedosto) },
            })
      if (vastaus.e === 'duplikaatti') {
        this.duplikaatteja++
        this._snackbar.open('Tosite on jo tässä setissä, ohitetaan', 'OK', { duration: 3000 })
        await this._aloitaKohde(this.indeksi() + 1)
        return
      }
      if (vastaus.e === 'tiedosto-liian-suuri') {
        this._snackbar.open(`Tiedosto on liian suuri: ${this._kohteenNimi(kohde)}`, 'OK', { duration: 6000 })
        return
      }
      if (vastaus.e) {
        this._snackbar.open(`Lisäys epäonnistui: ${vastaus.e}`, 'OK', { duration: 6000 })
        return
      }
      this.lisatty.update(lkm => lkm + 1)
      await this._aloitaKohde(this.indeksi() + 1)
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Lisäys epäonnistui', 'OK', { duration: 5000 })
    } finally {
      this.tallennetaan.set(false)
    }
  }

  async ohita() {
    if (this.tallennetaan()) {
      return
    }
    if (this.nykyinenJoValitussaSetissa) {
      this.duplikaatteja++
    } else {
      this.ohitettu.update(lkm => lkm + 1)
    }
    await this._aloitaKohde(this.indeksi() + 1)
  }

  lopeta() {
    if (this.tallennetaan()) {
      return
    }
    this._sulje()
  }

  kohteenNimi(kohde: LisattavaTositeKohde | null): string {
    return kohde ? this._kohteenNimi(kohde) : ''
  }

  private _kohteenNimi(kohde: LisattavaTositeKohde) {
    return kohde.tyyppi === 'lemonaid' ? `${kohde.asiakasNimi} / ${kohde.tositeAvain}` : kohde.tiedosto.name
  }

  private _nykyisenJasenyydet() {
    const kohde = this.nykyinenKohde
    return kohde?.tyyppi === 'lemonaid' ? (this.jasenyydet().get(kohde.tositeAvain) ?? []) : []
  }

  private async _lataaJasenyydet() {
    const asiakasAvaimet = new Set<string>()
    for (const kohde of this.data.kohteet) {
      if (kohde.tyyppi === 'lemonaid') {
        asiakasAvaimet.add(kohde.asiakasAvain)
      }
    }
    if (!asiakasAvaimet.size) {
      return
    }
    try {
      const vastaukset = await Promise.all(Array.from(asiakasAvaimet).map(asiakasAvain => callLlmTositeLukuTestaus(this._firebase, 'haeAsiakkaanSettijasenyydet', { asiakasAvain })))
      this.jasenyydet.set(ryhmitteleSettijasenyydetTositteittain(vastaukset.flatMap(vastaus => vastaus.jasenyydet ?? [])))
    } catch (err) {
      this._errorHandler.handleError(err)
    }
  }

  private _esitaytaMuustaSetista() {
    const muu = this.nykyisenMuuJasenyys
    if (!muu) {
      return
    }
    this.metatiedotForm.patchValue({
      toimittaja: muu.metatiedot.toimittaja ?? '',
      businessId: muu.metatiedot.businessId ?? '',
      paivamaara: muu.metatiedot.paivamaara ?? '',
      summa: muu.metatiedot.summa ?? null,
      toimittajaValinta: metatiedotToimittajaValinnaksi(muu.metatiedot),
      tyyppi: muu.metatiedot.tyyppi ?? '',
    })
    this.metatiedotForm.markAsDirty()
  }

  private async _aloitaKohde(indeksi: number) {
    this._vapautaEsikatselu()
    this.viitetiedot.set(null)
    this.toimittajaRekisteri.set([])
    this.naytettavatTositteetJaSivut.set(null)
    this.metatiedotForm.reset({ toimittaja: '', businessId: '', paivamaara: '', summa: null, toimittajaValinta: TOIMITTAJA_VALINTA_AUTOMAATTINEN, tyyppi: '' })
    if (indeksi >= this.data.kohteet.length) {
      this._sulje()
      return
    }
    this.indeksi.set(indeksi)
    this._paivitaOdotettuToimittajaTeksti()
    const kohde = this.data.kohteet[indeksi]
    if (kohde.tyyppi === 'ladattu') {
      this.esikatseluOnPdf.set(kohde.mediaTyyppi === 'application/pdf')
      this.naytettavatTositteetJaSivut.set([])
      if (this.esikatseluOnPdf()) {
        const dataUrl = await this._tiedostojenLataamisService.getAsDataUrl(kohde.tiedosto)
        if (this.indeksi() === indeksi) {
          this.esikatseluPdfUrl.set(dataUrl)
        }
      } else {
        this._objectUrl = URL.createObjectURL(kohde.tiedosto)
        this.esikatseluUrl.set(this._sanitizer.bypassSecurityTrustResourceUrl(this._objectUrl))
      }
      return
    }
    this._esitaytaMuustaSetista()
    this.viitetiedotLataa.set(true)
    this._paivitaOdotettuToimittajaTeksti()
    await Promise.all([this._lataaTosite(kohde, indeksi), this._lataaViitetiedot(kohde, indeksi)])
  }

  private async _lataaTosite(kohde: Extract<LisattavaTositeKohde, { tyyppi: 'lemonaid' }>, indeksi: number) {
    try {
      const uri = this._kirjanpitoUriService.annaKirjattavanFirestoreTositteenUriAvaimella(kohde.asiakasAvain, kohde.tositeAvain)
      const kirjattavaKuitti = await this._firebase.firestoreDoc<KirjattavaKuitti>(uri).get()
      const tosite = kirjattavaKuitti ? await this._kirjanpitoImageHandlerService.muutaKuittiTositteeksi({ avain: kohde.asiakasAvain, asiakasId: Number(kohde.asiakasId) }, kirjattavaKuitti) : null
      if (this.indeksi() !== indeksi) {
        return
      }
      this.naytettavatTositteetJaSivut.set(tosite ? [tosite] : [])
    } catch (err) {
      this._errorHandler.handleError(err)
      if (this.indeksi() === indeksi) {
        this.naytettavatTositteetJaSivut.set([])
      }
    }
  }

  /** Päivämäärä ja summa ovat asiakkaan syöttämiä, eivät mallin lukemia, joten ne esitäytetään. Toimittaja ja tunnus jätetään käsin syötettäviksi. */
  private async _lataaViitetiedot(kohde: Extract<LisattavaTositeKohde, { tyyppi: 'lemonaid' }>, indeksi: number) {
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'haeTositteenViitetiedot', { asiakasAvain: kohde.asiakasAvain, tositeAvain: kohde.tositeAvain })
      if (this.indeksi() !== indeksi) {
        return
      }
      this.viitetiedot.set(vastaus.tositteenTiedot ?? null)
      this.toimittajaRekisteri.set(vastaus.toimittajaRekisteri ?? [])
      if (this.viitetiedot() && this.metatiedotForm.pristine) {
        this.metatiedotForm.patchValue({ paivamaara: this.viitetiedot().paivamaara ?? '', summa: this.viitetiedot().summa })
      }
      this._paivitaOdotettuToimittajaTeksti()
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      if (this.indeksi() === indeksi) {
        this.viitetiedotLataa.set(false)
        this._paivitaOdotettuToimittajaTeksti()
      }
    }
  }

  private _paivitaOdotettuToimittajaTeksti() {
    this.odotettuToimittajaTeksti.set(
      laskeOdotettuToimittajaTeksti(
        this._metatiedotService,
        this.nykyinenKohde?.tyyppi === 'lemonaid',
        this.viitetiedotLataa(),
        this.toimittajaRekisteri(),
        lueMetatiedotLomakkeelta(this.metatiedotForm),
      ),
    )
  }

  private _vapautaEsikatselu() {
    if (this._objectUrl) {
      URL.revokeObjectURL(this._objectUrl)
      this._objectUrl = null
    }
    this.esikatseluUrl.set(null)
    this.esikatseluPdfUrl.set(null)
    this.esikatseluOnPdf.set(false)
  }

  private _sulje() {
    const yhteenveto: string[] = [`Lisättiin ${this.lisatty()} testitapausta settiin "${this.settiNimi()}"`]
    if (this.duplikaatteja) {
      yhteenveto.push(`${this.duplikaatteja} oli jo setissä`)
    }
    if (this.ohitettu()) {
      yhteenveto.push(`${this.ohitettu()} ohitettiin`)
    }
    if (this.settiAvain()) {
      this._snackbar.open(yhteenveto.join(', '), 'OK', { duration: 6000 })
    }
    this._dialogRef.close({ lisatty: this.lisatty() })
  }
}
